import React, { ReactNode } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import KenColors from "./kenColors";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
    fullHD: true;
    ultraHD: true;
  }
}

interface GlobalThemeProviderProps {
  children: ReactNode;
}

const theme = createTheme({
  palette: {
    KenColors: { ...KenColors },
    primary: {
      main: KenColors.ClientBlue,
    },
  },
  typography: {
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontFamily: ["GeneralSans"].join(","),
    body1: {
      fontSize: "16",
    },
    body2: {
      fontSize: 18,
      fontWeight: 500,
    },
    caption: {
      fontSize: "14px",
    },
    h1: {
      fontSize: "48px",
    },
    h2: {
      fontSize: "40px",
    },
    h3: {
      fontSize: "32px",
    },
    h4: {
      fontSize: "24px",
    },
    h5: {
      fontSize: "16px",
    },
    h6: {
      fontSize: "8px",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        elevation: {
          background: "#fff",
          boxShadow: "none",
          borderRadius: "12px",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        sx: {
          boxShadow: "none",
          borderRadius: "8px",
          padding: "6px 14px",
          textTransform: "none",
        },
      },
      variants: [
        {
          props: { variant: "contained" },
          style: {
            background: KenColors.ClientPrimary,
            color: KenColors.kenWhite,
            "& .disabled": {
              color: `${KenColors.kenWhite} !important`,
            },
          },
        },
        {
          props: { variant: "containedII" },
          style: {
            background: KenColors.ClientPrimaryGrey,
            color: KenColors.ClientBlack2,
            "& .disabled": {
              color: `${KenColors.ClientBlack2} !important`,
            },
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            border: `1px solid ${KenColors.ClientBlue7}`,
            background: KenColors.ClientBlue,
            color: KenColors.ClientBlue7,
            ":hover": {
              border: `1px solid ${KenColors.ClientBlue7}`,
              background: KenColors.ClientBlue,
              color: KenColors.ClientBlue7,
            },
          },
        },
        {
          props: { variant: "text" },
          style: {
            color: KenColors.ClientBlue3,
          },
        },
      ],
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: "GeneralSans",
        },
      },
      variants: [
        {
          props: { variant: "extralargebold" },
          style: {
            fontSize: "1.5rem",
            fontWeight: "600",
          },
        },
        {
          props: { variant: "extralargemedium" },
          style: {
            fontSize: "1.5rem",
            fontWeight: "500",
          },
        },
        {
          props: { variant: "extralargethin" },
          style: {
            fontSize: "1.5rem",
            fontWeight: "400",
          },
        },
        {
          props: { variant: "largebold" },
          style: {
            fontSize: "1.25rem",
            fontWeight: "600",
          },
        },
        {
          props: { variant: "largemedium" },
          style: {
            fontSize: "1.25rem",
            fontWeight: "500",
          },
        },
        {
          props: { variant: "largethin" },
          style: {
            fontSize: "1.25rem",
            fontWeight: "400",
          },
        },
        {
          props: { variant: "mediumbold" },
          style: {
            fontSize: "1rem",
            fontWeight: "600",
          },
        },
        {
          props: { variant: "medium" },
          style: {
            fontSize: "1rem",
            fontWeight: "500",
          },
        },
        {
          props: { variant: "mediumthin" },
          style: {
            fontSize: "1rem",
            fontWeight: "400",
          },
        },
        {
          props: { variant: "smallbold" },
          style: {
            fontSize: "0.875rem",
            fontWeight: "600",
          },
        },
        {
          props: { variant: "smallmedium" },
          style: {
            fontSize: "0.875rem",
            fontWeight: "500",
          },
        },
        {
          props: { variant: "smallthin" },
          style: {
            fontSize: "0.875rem",
            fontWeight: "400",
          },
        },
        {
          props: { variant: "extrasmallbold" },
          style: {
            fontSize: "0.75rem",
            fontWeight: "600",
          },
        },
        {
          props: { variant: "extrasmallmedium" },
          style: {
            fontSize: "0.75rem",
            fontWeight: "500",
          },
        },
        {
          props: { variant: "extrasmallthin" },
          style: {
            fontSize: "0.75rem",
            fontWeight: "400",
          },
        },
      ],
    },
  },
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (phones)
      sm: 576, // Small devices (landscape phones)
      md: 768, // Medium devices (tablets)
      lg: 992, // Large devices (desktops)
      xl: 1200, // Extra large devices (large desktops)
      xxl: 1440, // Extra wide desktops (e.g., 1440px width monitors)
      fullHD: 1710, // Full HD (1920px width) or larger screens like wide desktop monitors
      ultraHD: 1920, // 4k or larger monitors
    },
  },
});

const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({ children }) => {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default GlobalThemeProvider;
